// Procedural humanoid: the chunky low-poly look lives in characterGeometry.ts (one merged, rigidly skinned
// mesh per team colour); this file is the Bone hierarchy and the fully procedural animator (IK, gait, death).
//
// Conventions
//   Rig space: feet at y = 0 on the ground, the character faces -Z (rotate `root.rotation.y` by the
//   player's yaw), +X is the character's right. 1.8 m tall, eyes at 1.62 m.
//   CharacterAnimInput.velLocal is the horizontal velocity in rig space (m/s): +x = right, -z = forward.
//   wallrun: -1 = wall on the left, +1 = wall on the right.
//   `crouch` is 0 (standing) .. 1 (fully crouched).
//   setWeapon() takes a weapon root (WeaponModel.root): the named children "rightHandGrip", "leftHandGrip"
//   and "magazine" produced by weapons.ts are used to place the hands.

import * as THREE from "three";
import { teamColor } from "../materials/palette";
import { createTeamMaterial } from "../materials/materials";
import { setShield } from "../materials/characterShader";
import {
  ANKLE_H,
  ARM_L,
  ARM_U,
  BONE_LAYOUT,
  BONE_NAMES,
  HIP_X,
  HIP_Y,
  REST_INVERSES,
  SHIN,
  THIGH,
  getCharacterGeometry,
} from "./characterGeometry";
import type { BoneName } from "./characterGeometry";

/** Frees the shared character geometry (all rigs must be disposed first). */
export { disposeCharacterGeometry as disposeCharacterCache } from "./characterGeometry";

export interface CharacterAnimInput {
  velLocal: { x: number; z: number };
  speed: number;
  onGround: boolean;
  crouch: number;
  sliding: boolean;
  wallrun: -1 | 0 | 1;
  mantling: boolean;
  ads: boolean;
  pitch: number;
  firing: boolean;
  reloading: boolean;
  alive: boolean;
  deathTime: number;
  /** Spawn protected: the body shimmers (faded in and out by the rig). Absent: not protected. */
  shielded?: boolean;
}

export interface CharacterBones {
  body: THREE.Group;
  hips: THREE.Bone;
  spine: THREE.Bone;
  chest: THREE.Bone;
  neck: THREE.Bone;
  head: THREE.Bone;
  upperArmL: THREE.Bone;
  lowerArmL: THREE.Bone;
  handL: THREE.Bone;
  upperArmR: THREE.Bone;
  lowerArmR: THREE.Bone;
  handR: THREE.Bone;
  upperLegL: THREE.Bone;
  lowerLegL: THREE.Bone;
  footL: THREE.Bone;
  upperLegR: THREE.Bone;
  lowerLegR: THREE.Bone;
  footR: THREE.Bone;
  weaponMount: THREE.Group;
  /** Eye position (1.62 m when standing), a child of the head bone. */
  eyes: THREE.Object3D;
}

export interface CharacterRig {
  root: THREE.Group;
  setWeapon(model: THREE.Object3D | null): void;
  update(a: CharacterAnimInput, dt: number): void;
  setVisible(v: boolean): void;
  dispose(): void;
  readonly bones: CharacterBones;
}

export interface CharacterOpts {
  team: "a" | "b" | "none";
  accent?: string;
  enemy?: boolean;
}

const RUN_SPEED = 7.6;

// ---------------------------------------------------------------------------------------------
// Math scratch

const AXIS_X = new THREE.Vector3(1, 0, 0);
const AXIS_Z = new THREE.Vector3(0, 0, 1);
const DOWN = new THREE.Vector3(0, -1, 0);
const ONE = new THREE.Vector3(1, 1, 1);
const _d = new THREE.Vector3();
const _p = new THREE.Vector3();
const _e = new THREE.Vector3();
const _t = new THREE.Vector3();
const _u = new THREE.Vector3();
const _o = new THREE.Vector3();
const _g = new THREE.Vector3();
const _s = new THREE.Vector3();
const _sc = new THREE.Vector3();
const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();
const _qc = new THREE.Quaternion();
const _qd = new THREE.Quaternion();
const _qLower = new THREE.Quaternion();
const _ma = new THREE.Matrix4();
const _mb = new THREE.Matrix4();
const _inv = new THREE.Matrix4();

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function smooth(t: number): number {
  const c = clamp01(t);
  return c * c * (3 - 2 * c);
}

/**
 * Two-bone IK in the parent space of `upper`. `origin`, `target` and `pole` are in that space; the
 * knee/elbow bends toward `pole`. Leaves the lower bone's parent-space orientation in `outLower`.
 */
function solveLimb(
  upper: THREE.Object3D,
  lower: THREE.Object3D,
  lenA: number,
  lenB: number,
  origin: THREE.Vector3,
  target: THREE.Vector3,
  pole: THREE.Vector3,
  outLower: THREE.Quaternion,
): void {
  _d.subVectors(target, origin);
  let dist = _d.length();
  if (dist < 1e-6) {
    _d.set(0, -1, 0);
    dist = 1e-6;
  } else {
    _d.divideScalar(dist);
  }
  dist = Math.min(Math.max(dist, Math.abs(lenA - lenB) + 0.02), (lenA + lenB) * 0.9995);
  const x = (dist * dist + lenA * lenA - lenB * lenB) / (2 * dist);
  const h = Math.sqrt(Math.max(lenA * lenA - x * x, 0));
  _p.copy(pole).addScaledVector(_d, -pole.dot(_d));
  if (_p.lengthSq() < 1e-6) _p.set(1, 0, 0).addScaledVector(_d, -_d.x);
  _p.normalize();
  _e.copy(origin).addScaledVector(_d, x).addScaledVector(_p, h);
  _u.subVectors(_e, origin).normalize();
  upper.quaternion.setFromUnitVectors(DOWN, _u);
  _t.copy(origin).addScaledVector(_d, dist).sub(_e).normalize();
  outLower.setFromUnitVectors(DOWN, _t);
  _qa.copy(upper.quaternion).invert();
  lower.quaternion.copy(_qa).multiply(outLower);
}

// ---------------------------------------------------------------------------------------------
// Poses

const DEAD_UPPER_ARM_R = new THREE.Quaternion().setFromEuler(new THREE.Euler(0.2, 0, 1.0));
const DEAD_UPPER_ARM_L = new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.1, 0, -1.2));
const DEAD_LOWER_ARM = new THREE.Quaternion().setFromEuler(new THREE.Euler(0.4, 0, 0));
const DEAD_UPPER_LEG_R = new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.15, 0, 0.18));
const DEAD_UPPER_LEG_L = new THREE.Quaternion().setFromEuler(new THREE.Euler(0.45, 0, -0.12));
const DEAD_LOWER_LEG_L = new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.9, 0, 0));
const DEAD_HEAD = new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.25, 0.3, 0.1));
const IDENT = new THREE.Quaternion();

/** Right-hand grip relative to the chest pivot while hip-firing, by weapon style. */
const HOLD_RIFLE = new THREE.Vector3(0.2, -0.02, -0.26);
const HOLD_PISTOL = new THREE.Vector3(0.12, 0.05, -0.5);
const HOLD_KNIFE = new THREE.Vector3(0.22, -0.1, -0.42);
/** Sight position relative to the chest pivot when aiming down sights. */
const ADS_RIFLE = new THREE.Vector3(0.03, 0.38, -0.27);
const ADS_PISTOL = new THREE.Vector3(0.03, 0.38, -0.36);
/** Pivot (relative to the chest pivot) the weapon pitches around. */
const AIM_PIVOT = new THREE.Vector3(0.12, 0.2, 0);

const RELOAD_LEN = 1.9;

interface HeldWeapon {
  object: THREE.Object3D;
  rightGrip: THREE.Vector3;
  leftGrip: THREE.Vector3;
  sight: THREE.Vector3;
  leftHold: string;
  magazine: THREE.Object3D | null;
  magBase: THREE.Vector3;
  magPos: THREE.Vector3;
  pistolLike: boolean;
  knifeLike: boolean;
}

interface AnimState {
  time: number;
  phase: number;
  move: number;
  gait: number;
  crouch: number;
  slide: number;
  air: number;
  wallL: number;
  wallR: number;
  mantle: number;
  ads: number;
  pitch: number;
  hx: number;
  hz: number;
  recoil: number;
  kickTimer: number;
  wasFiring: boolean;
  reloadT: number;
  reloadBlend: number;
  dead: boolean;
  fall: number;
  fallDir: number;
  fadeApplied: boolean;
  /** Spawn-protection shimmer, 0..1. */
  shield: number;
}

function newState(): AnimState {
  return {
    time: 0, phase: 0, move: 0, gait: 0, crouch: 0, slide: 0, air: 0, wallL: 0, wallR: 0, mantle: 0, ads: 0,
    pitch: 0, hx: 0, hz: -1, recoil: 0, kickTimer: 0, wasFiring: false, reloadT: 0, reloadBlend: 0,
    dead: false, fall: 0, fallDir: 1, fadeApplied: false, shield: 0,
  };
}

/** Piecewise smooth interpolation through keyframes (t ascending, 0..1). */
function pathAt(keys: ReadonlyArray<readonly [number, THREE.Vector3]>, t: number, out: THREE.Vector3): void {
  if (t <= keys[0][0]) {
    out.copy(keys[0][1]);
    return;
  }
  for (let i = 1; i < keys.length; i++) {
    if (t <= keys[i][0]) {
      const a = keys[i - 1];
      const b = keys[i];
      out.lerpVectors(a[1], b[1], smooth((t - a[0]) / (b[0] - a[0])));
      return;
    }
  }
  out.copy(keys[keys.length - 1][1]);
}

const _k0 = new THREE.Vector3();
const _k1 = new THREE.Vector3();
const _k2 = new THREE.Vector3();
const _k3 = new THREE.Vector3();
const _hand = new THREE.Vector3();
const _holdO = new THREE.Vector3();
const _adsO = new THREE.Vector3();
const _rightT = new THREE.Vector3();
const _leftT = new THREE.Vector3();
const _pole = new THREE.Vector3();
const DEAD_MOUNT = new THREE.Quaternion().setFromEuler(new THREE.Euler(0.3, 0.2, 0.9));
const DEAD_MOUNT_POS = new THREE.Vector3(0.34, 0.05, -0.25);

/** Support-hand path (weapon space) for a reload at progress t. */
function reloadHandPath(held: HeldWeapon, t: number, out: THREE.Vector3): void {
  _k0.copy(held.leftGrip);
  _k1.copy(held.magPos);
  _k2.copy(held.magPos).add(_k3.set(0, -0.2, 0.03));
  pathAt(
    [
      [0, _k0],
      [0.16, _k0],
      [0.3, _k1],
      [0.46, _k2],
      [0.56, _k2],
      [0.72, _k1],
      [0.8, _k1],
      [0.95, _k0],
      [1, _k0],
    ],
    t,
    out,
  );
}

/** How far the magazine has dropped out of the weapon (m) and whether it is visible. */
function reloadMag(t: number): { drop: number; visible: boolean } {
  if (t < 0.3) return { drop: 0, visible: true };
  if (t < 0.46) return { drop: 0.2 * smooth((t - 0.3) / 0.16), visible: true };
  if (t < 0.56) return { drop: 0.2, visible: false };
  if (t < 0.72) return { drop: 0.2 * (1 - smooth((t - 0.56) / 0.16)), visible: true };
  return { drop: 0, visible: true };
}

// Fade support: the materials of held weapons are made transparent and scaled in place, and restored on revive.
const baseOpacity = new WeakMap<THREE.Material, number>();

/**
 * Scales the opacity of every material under `root` and keeps them transparent, so the fade only
 * changes a uniform (switching `transparent` later would rebuild the shader).
 */
function fadeObject(root: THREE.Object3D, opacity: number): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    for (const m of mats) {
      const base = baseOpacity.get(m) ?? m.opacity;
      baseOpacity.set(m, base);
      m.opacity = base * opacity;
      m.transparent = true;
    }
  });
}

// ---------------------------------------------------------------------------------------------

/** Frustum-culling sphere of the skinned mesh (rig space): covers every pose, including a slide and the collapsed death pose. */
const CULL_SPHERE = new THREE.Sphere(new THREE.Vector3(0, 0.9, 0), 2.2);

export function createCharacter(opts: CharacterOpts): CharacterRig {
  const primary = teamColor(opts.team, opts.enemy ?? false, opts.accent);
  const { material, uniforms } = createTeamMaterial(primary);

  const root = new THREE.Group();
  root.name = "character";

  const body = new THREE.Group();
  body.name = "body";
  root.add(body);
  const byName = {} as Record<BoneName, THREE.Bone>;
  for (const name of BONE_NAMES) {
    const def = BONE_LAYOUT[name];
    const b = new THREE.Bone();
    b.name = name;
    b.position.set(def.pos[0], def.pos[1], def.pos[2]);
    (def.parent === null ? body : byName[def.parent]).add(b);
    byName[name] = b;
  }
  const {
    hips, spine, chest, neck, head, upperArmL, lowerArmL, handL, upperArmR, lowerArmR, handR,
    upperLegL, lowerLegL, footL, upperLegR, lowerLegR, footR,
  } = byName;
  for (const g of [spine, chest, neck, head]) g.rotation.order = "YXZ";
  const weaponMount = new THREE.Group();
  weaponMount.name = "weaponMount";
  chest.add(weaponMount);
  const eyes = new THREE.Object3D();
  eyes.name = "eyes";
  eyes.position.set(0, 0.02, -0.12);
  head.add(eyes);

  // The whole character is one skinned mesh bound to the bones in their rest pose.
  const skeleton = new THREE.Skeleton(BONE_NAMES.map((n) => byName[n]), REST_INVERSES);
  const mesh = new THREE.SkinnedMesh(getCharacterGeometry(primary), material);
  mesh.name = "character:mesh";
  mesh.castShadow = true;
  mesh.boundingSphere = CULL_SPHERE.clone();
  root.add(mesh);
  mesh.bind(skeleton, new THREE.Matrix4());

  const bones: CharacterBones = {
    body, hips, spine, chest, neck, head, upperArmL, lowerArmL, handL, upperArmR, lowerArmR, handR,
    upperLegL, lowerLegL, footL, upperLegR, lowerLegR, footR, weaponMount, eyes,
  };

  const st = newState();
  let held: HeldWeapon | null = null;
  const mountAlivePos = new THREE.Vector3();
  const mountAliveQuat = new THREE.Quaternion();

  const setWeapon = (model: THREE.Object3D | null): void => {
    if (held !== null) {
      if (held.magazine !== null) {
        held.magazine.position.copy(held.magBase);
        held.magazine.visible = true;
      }
      fadeObject(held.object, 1); // a model swapped out mid-fade is cached and shown again later
      weaponMount.remove(held.object);
      held = null;
    }
    if (model === null) return;
    fadeObject(model, 1);
    const right = model.getObjectByName("rightHandGrip");
    const left = model.getObjectByName("leftHandGrip");
    const sightObj = model.getObjectByName("sightAnchor");
    const mag = model.getObjectByName("magazine") ?? null;
    const rightGrip = right ? right.position.clone() : new THREE.Vector3(0, -0.05, 0.03);
    const leftGrip = left ? left.position.clone() : new THREE.Vector3(0, 0, -0.3);
    const leftHold = typeof left?.userData.hold === "string" ? (left.userData.hold as string) : "cup";
    const rightHold = typeof right?.userData.hold === "string" ? (right.userData.hold as string) : "grip";
    const magBase = mag ? mag.position.clone() : new THREE.Vector3();
    held = {
      object: model,
      rightGrip,
      leftGrip,
      sight: sightObj ? sightObj.position.clone() : new THREE.Vector3(0, 0.08, 0.05),
      leftHold,
      magazine: mag,
      magBase,
      magPos: mag ? magBase.clone().add(new THREE.Vector3(0, -0.06, 0)) : leftGrip.clone().add(new THREE.Vector3(0, -0.1, 0)),
      pistolLike: leftGrip.distanceTo(rightGrip) < 0.2,
      knifeLike: rightHold === "handle",
    };
    model.position.set(0, 0, 0);
    model.quaternion.identity();
    model.scale.set(1, 1, 1);
    weaponMount.add(model);
  };

  const update = (a: CharacterAnimInput, dtIn: number): void => {
    const dt = Math.min(Math.max(dtIn, 0), 0.1);
    const k = (rate: number): number => 1 - Math.exp(-rate * dt);

    // ---- life cycle
    let fadeT = 0;
    if (!a.alive) {
      if (!st.dead) {
        st.dead = true;
        st.fallDir = st.move > 0.45 ? -1 : 1;
      }
      st.fall = smooth(a.deathTime / 0.55);
      fadeT = clamp01((a.deathTime - 2.2) / 0.8);
    } else if (st.dead) {
      st.dead = false;
      st.fall = 0;
    }
    if (fadeT > 0) {
      const o = 1 - fadeT;
      material.opacity = o;
      if (held !== null) fadeObject(held.object, o);
      st.fadeApplied = true;
    } else if (st.fadeApplied) {
      material.opacity = 1;
      if (held !== null) fadeObject(held.object, 1);
      st.fadeApplied = false;
    }
    body.visible = fadeT < 1;
    mesh.visible = body.visible;
    const shieldTarget = a.alive && a.shielded === true ? 1 : 0;
    // Fades in fast and out over a quarter second, so the end of the protection reads.
    st.shield += (shieldTarget - st.shield) * k(shieldTarget > st.shield ? 20 : 8);
    if (st.shield < 0.002) st.shield = 0;
    setShield(uniforms, st.shield, st.time);
    const live = a.alive;

    // ---- smoothed state
    st.time += dt;
    const speed = live ? a.speed : 0;
    const speedN = Math.min(speed / RUN_SPEED, 1.5);
    st.move += (speedN - st.move) * k(10);
    const grounded = a.onGround || a.wallrun !== 0;
    const gaitOn = live && grounded && !a.sliding && !a.mantling && speed > 0.35 ? 1 : 0;
    st.gait += (gaitOn - st.gait) * k(12);
    st.crouch += (clamp01(a.crouch) - st.crouch) * k(16);
    st.slide += ((live && a.sliding ? 1 : 0) - st.slide) * k(14);
    const airOn = live && !a.onGround && a.wallrun === 0 && !a.mantling ? 1 : 0;
    st.air += (airOn - st.air) * k(9);
    st.wallL += ((live && a.wallrun === -1 ? 1 : 0) - st.wallL) * k(10);
    st.wallR += ((live && a.wallrun === 1 ? 1 : 0) - st.wallR) * k(10);
    st.mantle += ((live && a.mantling ? 1 : 0) - st.mantle) * k(14);
    const reloading = live && a.reloading;
    st.ads += ((live && a.ads && !reloading ? 1 : 0) - st.ads) * k(14);
    st.pitch += (Math.min(Math.max(a.pitch, -1.45), 1.45) - st.pitch) * k(30);
    st.reloadT = reloading ? st.reloadT + dt : 0;
    st.reloadBlend += ((reloading ? 1 : 0) - st.reloadBlend) * k(12);

    const vx = a.velLocal.x;
    const vz = a.velLocal.z;
    const vlen = Math.hypot(vx, vz);
    if (vlen > 0.3) {
      st.hx += (vx / vlen - st.hx) * k(10);
      st.hz += (vz / vlen - st.hz) * k(10);
    }
    let hl = Math.hypot(st.hx, st.hz);
    if (hl < 0.2) {
      st.hx = 0;
      st.hz = -1;
      hl = 1;
    }
    const tx = st.hx / hl;
    const tz = st.hz / hl;

    const firing = live && a.firing;
    if (firing) {
      st.kickTimer -= dt;
      if (!st.wasFiring || st.kickTimer <= 0) {
        st.recoil = 1;
        st.kickTimer = 0.085;
      }
    }
    st.wasFiring = firing;
    st.recoil *= Math.exp(-18 * dt);

    // ---- gait
    const cadence = 1.5 + 0.15 * speed;
    if (gaitOn > 0) st.phase = (st.phase + cadence * dt) % 1;
    const moveR = Math.min(st.move, 1);
    const cycleLen = speed / cadence;
    const stance = 0.62 - 0.22 * moveR;
    const stride = Math.min((stance * cycleLen) / 2, 0.34) * st.gait;
    const liftH = (0.08 + 0.17 * moveR) * st.gait;
    const gaitAmp = st.gait * Math.min(st.move * 1.2, 1);

    const hipsLocalY = HIP_Y - 0.54 * st.crouch - 0.085 * moveR * st.gait;
    const bob = Math.sin(st.phase * Math.PI * 4) * 0.018 * gaitAmp;

    // ---- body transform: slide pitch and wall-run roll about the hips pivot
    const roll = (st.wallR - st.wallL) * 0.32;
    const bodyPitch = 1.2 * st.slide;
    _qa.setFromAxisAngle(AXIS_X, bodyPitch);
    _qb.setFromAxisAngle(AXIS_Z, roll);
    body.quaternion.copy(_qb).multiply(_qa);
    const hipsTargetY = hipsLocalY + (0.3 - hipsLocalY) * st.slide;
    _o.set(0, hipsLocalY, 0).applyQuaternion(body.quaternion);
    body.position.set(0, hipsTargetY - _o.y, 0.12 * st.slide - _o.z);

    hips.position.set(0, hipsLocalY + bob, 0);
    hips.rotation.set(0, 0.1 * Math.sin(st.phase * Math.PI * 2) * gaitAmp, 0.035 * Math.sin(st.phase * Math.PI * 4) * gaitAmp);

    // ---- torso and head
    const gunPose = held !== null && live ? 1 - st.mantle : 0;
    const twist = -(0.4 + 0.15 * st.ads) * gunPose;
    const lean = -0.1 * moveR * st.gait - 0.4 * st.crouch - 0.55 * st.mantle - 0.12 * st.air;
    const breathe = Math.sin(st.time * 1.7) * 0.012;
    const counterTwist = 0.12 * Math.sin(st.phase * Math.PI * 2) * gaitAmp;
    spine.rotation.set(lean * 0.55 + st.pitch * 0.15, twist * 0.45 + counterTwist, -Math.min(Math.max(vx / RUN_SPEED, -1), 1) * 0.05 * st.gait);
    chest.rotation.set(lean * 0.45 + st.pitch * 0.15 + breathe, twist * 0.55 - counterTwist * 0.5, 0);
    neck.rotation.set(st.pitch * 0.3 - lean * 0.5, -twist * 0.65, 0);
    head.rotation.set(st.pitch * 0.4, -twist * 0.35, 0);

    // ---- legs (IK in the hips frame)
    const kneePole = _s.set(0, 0, -1);
    for (const side of [1, -1] as const) {
      const upper = side === 1 ? upperLegR : upperLegL;
      const lower = side === 1 ? lowerLegR : lowerLegL;
      const foot = side === 1 ? footR : footL;
      const phi = (st.phase + (side === 1 ? 0.5 : 0)) % 1;
      let along: number;
      let up: number;
      let swing: number;
      if (phi < stance) {
        along = stride * (1 - (2 * phi) / stance);
        up = 0;
        swing = 0;
      } else {
        const u = (phi - stance) / (1 - stance);
        along = -stride + 2 * stride * smooth(u);
        swing = Math.sin(Math.PI * u);
        up = liftH * swing;
      }
      const groundY = ANKLE_H - (hipsLocalY + bob);
      _t.set(side * HIP_X + tx * along, groundY + up, tz * along);
      // Slide: right leg extended forward, left leg folded
      _g.set(side * HIP_X, side === 1 ? -0.8 : -0.46, side === 1 ? -0.02 : 0.05);
      _t.lerp(_g, st.slide);
      // Airborne tuck / mantle knee-up
      const tuck = Math.max(st.air * 0.9, st.mantle * 0.8);
      _g.set(side * HIP_X, side === 1 ? -0.5 : -0.56, side === 1 ? -0.2 : 0.1);
      _t.lerp(_g, tuck);
      // Wall-run: the wall-side foot plants against the wall
      _t.x += side * (side === 1 ? st.wallR : st.wallL) * 0.16;
      _o.set(side * HIP_X, 0, 0);
      solveLimb(upper, lower, THIGH, SHIN, _o, _t, kneePole, _qLower);
      _qd.setFromAxisAngle(AXIS_X, -0.35 * swing * (1 - st.slide));
      foot.quaternion.copy(_qLower).invert().multiply(_qd);
    }

    // ---- chest frame for the arms
    body.updateMatrix();
    hips.updateMatrix();
    spine.updateMatrix();
    chest.updateMatrix();
    _ma.copy(body.matrix).multiply(hips.matrix).multiply(spine.matrix).multiply(chest.matrix);
    _inv.copy(_ma).invert();
    const chestPos = _o.setFromMatrixPosition(_ma);
    const qChest = _qc.setFromRotationMatrix(_ma);

    // Weapon pose in rig space
    let hasRight = false;
    let hasLeft = false;
    if (held !== null && live) {
      const w = held;
      const hold = w.knifeLike ? HOLD_KNIFE : w.pistolLike ? HOLD_PISTOL : HOLD_RIFLE;
      const adsRel = w.pistolLike ? ADS_PISTOL : ADS_RIFLE;
      const rb = st.reloadBlend;
      const pitchW = (w.knifeLike ? 0.5 : 1) * st.pitch - 0.9 * st.mantle + 0.05 * st.recoil + 0.25 * rb;
      // Weapon origin without aim pitch
      _holdO.copy(chestPos).add(hold).sub(w.rightGrip);
      _adsO.copy(chestPos).add(adsRel).sub(w.sight);
      const origin = _holdO.lerp(_adsO, st.ads * (w.knifeLike ? 0 : 1));
      origin.x += -0.05 * rb;
      origin.y += -0.05 * rb - 0.35 * st.mantle + 0.004 * Math.sin(st.time * 1.6);
      origin.z += 0.1 * rb;
      // Pitch around the aim pivot
      _g.copy(chestPos).add(AIM_PIVOT);
      _qa.setFromAxisAngle(AXIS_X, pitchW);
      origin.sub(_g).applyQuaternion(_qa).add(_g);
      _qb.setFromAxisAngle(AXIS_Z, 0.3 * rb);
      _qa.multiply(_qb);
      // Recoil kick back along the weapon
      _s.set(0, 0.006 * st.recoil, 0.045 * st.recoil).applyQuaternion(_qa);
      origin.add(_s);
      // Run bob keeps the gun alive
      origin.y += 0.012 * Math.sin(st.phase * Math.PI * 4) * gaitAmp;
      origin.x += 0.008 * Math.sin(st.phase * Math.PI * 2) * gaitAmp;

      // Mount transform in chest space
      _mb.compose(origin, _qa, ONE);
      _ma.copy(_inv).multiply(_mb);
      _ma.decompose(weaponMount.position, weaponMount.quaternion, _sc);
      mountAlivePos.copy(weaponMount.position);
      mountAliveQuat.copy(weaponMount.quaternion);

      // Grip targets in chest space
      _rightT.copy(w.rightGrip).applyQuaternion(_qa).add(origin).applyMatrix4(_inv);
      hasRight = true;
      if (w.leftHold !== "none") {
        _leftT.copy(w.leftGrip);
        if (st.reloadBlend > 0.01 && st.reloadT > 0) {
          reloadHandPath(w, Math.min(st.reloadT / RELOAD_LEN, 1), _hand);
          _leftT.lerp(_hand, st.reloadBlend);
        }
        _leftT.applyQuaternion(_qa).add(origin).applyMatrix4(_inv);
        hasLeft = true;
      }
      // Magazine animation during a reload
      if (w.magazine !== null) {
        if (st.reloadBlend > 0.01 && st.reloadT > 0) {
          const m = reloadMag(Math.min(st.reloadT / RELOAD_LEN, 1));
          w.magazine.position.set(w.magBase.x, w.magBase.y - m.drop, w.magBase.z);
          w.magazine.visible = m.visible;
        } else {
          w.magazine.position.copy(w.magBase);
          w.magazine.visible = true;
        }
      }
    }

    // ---- arms (IK in the chest frame)
    const armSwing = 0.3 * gaitAmp;
    for (const side of [1, -1] as const) {
      const upper = side === 1 ? upperArmR : upperArmL;
      const lower = side === 1 ? lowerArmR : lowerArmL;
      const handBone = side === 1 ? handR : handL;
      const shoulder = upper.position;
      const gunTarget = side === 1 ? (hasRight ? _rightT : null) : hasLeft ? _leftT : null;
      // Unarmed / free hand: hangs and swings against the legs
      const swingZ = -side * armSwing * Math.cos(st.phase * Math.PI * 2);
      _t.set(shoulder.x + side * 0.04, shoulder.y - 0.5 + 0.1 * st.air, swingZ - 0.1 * st.air);
      // Mantle: reach up and forward
      _g.set(shoulder.x * 0.6, shoulder.y + 0.5, -0.48);
      _t.lerp(_g, st.mantle);
      if (gunTarget !== null) {
        // Pull the wrist slightly short of the grip so the palm lands on it
        _u.copy(gunTarget).sub(shoulder).normalize();
        _g.copy(gunTarget).addScaledVector(_u, -0.05);
        _t.lerp(_g, gunPose);
      }
      _pole.set(side * 0.7, -1, 0.4);
      solveLimb(upper, lower, ARM_U, ARM_L, shoulder, _t, _pole, _qLower);

      // Hand orientation follows the weapon
      if (gunTarget !== null && held !== null) {
        const w = held;
        _qa.copy(weaponMount.quaternion);
        _qb.copy(qChest).multiply(_qa);
        const hold = side === 1 ? (w.knifeLike ? "handle" : "grip") : w.leftHold;
        _qd.copy(IDENT);
        if (hold === "cup") _qd.setFromAxisAngle(AXIS_X, 1.3);
        if (hold === "handle") _qd.setFromAxisAngle(AXIS_X, 1.2);
        _qb.multiply(_qd);
        _qb.premultiply(_qa.copy(qChest).invert());
        handBone.quaternion.copy(_qLower).invert().multiply(_qb);
      } else {
        handBone.quaternion.set(0, 0, 0, 1);
      }
    }

    // ---- death: blend toward the collapsed pose
    const f = st.fall;
    if (f > 0) {
      const dir = st.fallDir;
      _qa.setFromAxisAngle(AXIS_X, dir * Math.PI * 0.49);
      body.quaternion.slerp(_qa, f);
      body.position.lerp(_g.set(0, 0.15, -0.9 * dir), f);
      upperArmR.quaternion.slerp(DEAD_UPPER_ARM_R, f);
      upperArmL.quaternion.slerp(DEAD_UPPER_ARM_L, f);
      lowerArmR.quaternion.slerp(DEAD_LOWER_ARM, f);
      lowerArmL.quaternion.slerp(DEAD_LOWER_ARM, f);
      handR.quaternion.slerp(IDENT, f);
      handL.quaternion.slerp(IDENT, f);
      upperLegR.quaternion.slerp(DEAD_UPPER_LEG_R, f);
      upperLegL.quaternion.slerp(DEAD_UPPER_LEG_L, f);
      lowerLegR.quaternion.slerp(IDENT, f);
      lowerLegL.quaternion.slerp(DEAD_LOWER_LEG_L, f);
      footR.quaternion.slerp(IDENT, f);
      footL.quaternion.slerp(IDENT, f);
      head.quaternion.slerp(DEAD_HEAD, f);
      weaponMount.position.copy(mountAlivePos).lerp(DEAD_MOUNT_POS, f);
      weaponMount.quaternion.copy(mountAliveQuat).slerp(DEAD_MOUNT, f);
    }
  };

  return {
    root,
    bones,
    setWeapon,
    update,
    setVisible(v: boolean): void {
      root.visible = v;
    },
    dispose(): void {
      root.removeFromParent();
      if (held !== null) {
        weaponMount.remove(held.object);
        held = null;
      }
      skeleton.dispose();
      material.dispose();
    },
  };
}
